import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Note, ArrowsClockwise, Camera, Receipt, BellRinging, UsersThree, MagnifyingGlass, Pulse } from "@phosphor-icons/react";
import api from "@/lib/api";
import { JOB_STATUS_LABELS, INVOICE_STATUS_LABELS, inr } from "@/lib/constants";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const TYPES = {
  all: "All Activity",
  job: "Job Updates",
  client: "Client Communication",
  invoice: "Invoice Activity",
  team: "Team Activity",
  system: "System Activity",
};

const META = {
  job: { icon: ArrowsClockwise, tone: "bg-primary/10 text-primary" },
  client: { icon: Note, tone: "bg-accent/15 text-accent" },
  invoice: { icon: Receipt, tone: "bg-emerald-100 text-emerald-700" },
  team: { icon: UsersThree, tone: "bg-blue-100 text-blue-700" },
  system: { icon: BellRinging, tone: "bg-amber-100 text-amber-700" },
};

export default function Activity() {
  const [params, setParams] = useSearchParams();
  const [jobEvents, setJobEvents] = useState([]);
  const [audit, setAudit] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const type = params.get("type") || "all";
  const actor = params.get("actor") || "all";

  useEffect(() => {
    Promise.all([
      api.get("/activity").then((r) => r.data).catch(() => []),
      api.get("/audit", { params: { limit: 300 } }).then((r) => r.data).catch(() => []),
      api.get("/invoices").then((r) => r.data).catch(() => []),
      api.get("/team").then((r) => r.data).catch(() => []),
    ]).then(([a, b, c, d]) => { setJobEvents(a); setAudit(b); setInvoices(c); setTeam(d); setLoading(false); });
  }, []);

  const events = useMemo(() => {
    const out = [];
    jobEvents.forEach((e, i) => out.push({
      key: `job-${i}-${e.at}`, kind: "job", at: e.at, actor_id: e.by, actor: e.by_name,
      title: e.type === "note" ? `${e.by_name || "Someone"} added a site note` : `Status set to ${JOB_STATUS_LABELS[e.status] || e.status}`,
      body: e.text || "", has_photo: e.has_photo,
      link: "/app/jobs", linkLabel: `${e.reference} · ${e.applicant_name}`, raw: e,
    }));
    audit.forEach((a) => {
      const isInvoice = (a.entity === "invoices") || a.action.startsWith("invoice.");
      const isTeam = a.action.startsWith("auth.") || a.entity === "users";
      const isClient = a.entity === "clients";
      const kind = isInvoice ? "invoice" : isTeam ? "team" : isClient ? "client" : "system";
      out.push({
        key: `a-${a.id}`, kind, at: a.at, actor_id: a.actor_id, actor: a.actor_name,
        title: `${a.actor_name || "Someone"} · ${a.action}`, body: a.detail || "",
        link: isInvoice ? "/app/invoices" : isClient ? "/app/clients" : "/app/team/log",
        linkLabel: a.entity || "", raw: a,
      });
    });
    invoices.forEach((i) => {
      if (!i.created_at) return;
      out.push({
        key: `inv-${i.id}`, kind: "invoice", at: i.created_at, actor_id: i.created_by, actor: null,
        title: `Invoice ${i.invoice_number} raised · ${INVOICE_STATUS_LABELS[i.status] || i.status}`,
        body: `${i.bill_to_name} · ${inr(i.total)}`,
        link: "/app/invoices", linkLabel: i.invoice_number, raw: i,
      });
    });
    return out.sort((a, b) => String(b.at).localeCompare(String(a.at)));
  }, [jobEvents, audit, invoices]);

  const filtered = events.filter((e) => {
    if (type !== "all" && e.kind !== type) return false;
    if (actor !== "all" && e.actor_id !== actor) return false;
    const s = q.toLowerCase();
    return !s || `${e.title} ${e.body} ${e.linkLabel} ${e.actor || ""}`.toLowerCase().includes(s);
  });

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === "all") next.delete(k); else next.set(k, v);
    setParams(next);
  };

  if (loading) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading activity…</div>;

  return (
    <div className="space-y-5" data-testid="activity-feed">
      <PageHeader overline="At a glance" title="Activity Feed" subtitle={`${filtered.length} events · one chronological trail across the firm`} />

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search keyword, job reference or client…" className="pl-9" data-testid="activity-search" />
        </div>
        <Select value={type} onValueChange={(v) => setParam("type", v)}>
          <SelectTrigger className="w-[190px]" data-testid="activity-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={actor} onValueChange={(v) => setParam("actor", v)}>
          <SelectTrigger className="w-[200px]" data-testid="activity-actor-filter"><SelectValue placeholder="Everyone" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Everyone</SelectItem>
            {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Pulse}>No activity matches these filters yet.</EmptyState>
      ) : (
        <div className="space-y-2">
          {filtered.slice(0, 150).map((e, i) => {
            const m = META[e.kind] || META.system;
            return (
              <Card key={e.key} className="p-3 flex items-start gap-3 border-border cursor-pointer transition-transform duration-150 hover:-translate-y-0.5 hover:shadow"
                onClick={() => setDetail(e)} data-testid={`activity-item-${i}`}>
                <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${m.tone}`}>
                  <m.icon size={18} weight="duotone" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-primary">{e.title}</div>
                  {e.body && <div className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap line-clamp-2">{e.body}</div>}
                  {e.has_photo && <div className="text-xs text-accent mt-0.5 inline-flex items-center gap-1"><Camera size={12} /> photo attached</div>}
                  {e.linkLabel && <div className="text-[11px] font-mono text-muted-foreground mt-0.5">{e.linkLabel}</div>}
                </div>
                <div className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
                  {new Date(e.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Activity Detail</DialogTitle>
            <DialogDescription>{detail ? new Date(detail.at).toLocaleString("en-IN", { dateStyle: "full", timeStyle: "short" }) : ""}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3" data-testid="activity-detail">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">What happened</div>
                <div className="text-sm text-primary mt-0.5">{detail.title}</div>
              </div>
              {detail.actor && (
                <div><div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Who</div><div className="text-sm text-primary mt-0.5">{detail.actor}</div></div>
              )}
              {detail.body && (
                <div><div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Details</div><div className="text-sm text-muted-foreground mt-0.5 whitespace-pre-wrap">{detail.body}</div></div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Category</div>
                <div className="text-sm text-primary mt-0.5">{TYPES[detail.kind] || detail.kind}</div>
              </div>
              {detail.link && (
                <Link to={detail.link} className="inline-block text-sm text-accent hover:underline" data-testid="activity-detail-link">
                  Open linked record{detail.linkLabel ? ` (${detail.linkLabel})` : ""} →
                </Link>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
